// src/pages/Main/index.js

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Container } from 'react-bootstrap';
import { AppCard, ComposeCard, GalleryCard, Pill } from 'pages/Main/frontCards';
import { APPS, GALLERY, PRIMITIVES, QUESTIONS, SURFACES } from 'pages/Main/frontContent';
import { CodeCard, POLICY, PYTHON, TERMINAL } from 'pages/Main/frontCode';
import FrontShot from 'pages/Main/frontShot';
import Stage from 'pages/Main/stage';
import { NOTEBOOKS_URL } from 'blob_types/blobhub/model/onnx/sdkSnippets';
import Title from 'context/title';
import "./index.css";

const DOCS_URL = "https://docs.blobhub.io";
const CREATE_ORG_PATH = "/p/me/org/create";

const External = ({ href, className, children }) => (
  <a href={href} className={className} target="_blank" rel="noopener noreferrer">{children}</a>
);

const PrimaryCta = ({ authenticated }) => (authenticated
  ? <Link className="btn btn-success" to={CREATE_ORG_PATH}>Create an organization</Link>
  : <External href={DOCS_URL} className="btn btn-success">Get started</External>);

const BandHead = ({ eyebrow, title, text, to, more }) => (
  <div className="fp-band-head">
    <div>
      <span className="fp-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
    {to && <Link className="fp-more" to={to}>{more} &rarr;</Link>}
  </div>
);

// One shot of a composition: `float` overlaps the main shot, and `pin` labels it.
const Shot = ({ float, pin, children }) => (
  <div className={float ? `fp-comp-float fp-f-${float}` : "fp-comp-main"}>
    {pin}
    <div className="fp-shot">{children}</div>
  </div>
);

const Pillar = ({ kind, flip, eyebrow, title, lede, text, rows, caption, children }) => (
  <section className={`fp-pillar fp-pillar-${kind} fp-t-${kind}${flip ? " fp-flip" : ""}`}>
    <Container className="fp-pillar-grid">
      <div className="fp-pillar-text">
        <span className="fp-eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        <h3>{lede}</h3>
        <p>{text}</p>
        <ul className="fp-rows">
          {rows.map(([row, note]) => <li key={row}>{row}{note && <small>{note}</small>}</li>)}
        </ul>
      </div>
      <div>
        <div className="fp-comp">{children}</div>
        <p className="fp-caption">{caption}</p>
      </div>
    </Container>
  </section>
);

const SurfaceText = ({ surface }) => {
  const word = "notebooks";
  if ("sdk" !== surface.key || !surface.text.includes(word)) {
    return surface.text;
  }
  const [before, after] = surface.text.split(word);
  return <>{before}<External href={NOTEBOOKS_URL}>{word}</External>{after}</>;
};

const Hero = ({ onStart }) => (
  <Stage kind="hero">
    <Container>
      <span className="fp-eyebrow">The one-stop shop for agentic development</span>
      <h1 className="fp-hero-title">
        <span className="fp-soft">Everything your company needs to build agents that are</span> consistent and easily
        talk to each other.
      </h1>
      <p className="fp-hero-sub">
        Frameworks help you write an agent. BlobHub is where your agents live and work, inside guardrails you set:
        what each one is, what it may do, and what it did are on the record from its first run.
      </p>
      <div className="hero-cta">
        <Button variant="success" onClick={onStart}>Start building</Button>
        <Link className="btn btn-light" to="/gallery">Explore the gallery</Link>
        <span className="fp-install">or pip install blobhub-cli</span>
      </div>
      <div className="fp-comp hero-visual">
        {/* The hero's pictures sit on its dark stage in both themes, so they are always the dark captures. */}
        <Shot pin={<span className="fp-pin fp-stage-tag fp-t-build">Build</span>}>
          <FrontShot name="hero_editor" loading="eager" fixedTheme="dark"
            alt={"The workflow editor showing a support-ticket triage workflow: Intake and Route code steps around a "
              + "Classify step that calls Claude through the Anthropic connector."} />
        </Shot>
        <Shot float="govern" pin={<span className="fp-pin fp-stage-tag fp-t-govern">Govern</span>}>
          <div className="fp-fadeout">
            <FrontShot name="hero_ask" loading="eager" fixedTheme="dark"
              alt={"An agent stopped mid-run to ask a person whether to map the CLI or the macro renderer first; "
                + "the cell waits for the answer."} />
          </div>
        </Shot>
        <Shot float="audit" pin={<span className="fp-pin fp-stage-tag fp-t-audit">Audit</span>}>
          <div className="fp-crop">
            <FrontShot name="hero_events" loading="eager" fixedTheme="dark"
              alt={"The execution event log: each step entered and exited, with its route and duration."} />
          </div>
        </Shot>
      </div>
    </Container>
  </Stage>
);

const Position = () => (
  <section className="fp-band">
    <Container>
      <h2 className="fp-tagline" aria-label="Build. Govern. Audit.">
        <span className="fp-t-build">Build.</span>
        <span className="fp-t-govern">Govern.</span>
        <span className="fp-t-audit">Audit.</span>
      </h2>
      <p className="fp-position">
        BlobHub is <b>the system of record and action for your agents</b>: where they are built, governed and audited.
      </p>
      <div className="fp-grid fp-g3 fp-questions">
        {QUESTIONS.map((question) => (
          <Card key={question.key}>
            <Card.Header className="fp-card-header">
              <span className={`fp-dot fp-t-${question.key}`}>{question.label}</span>
              <span className="fp-card-reset">{question.when}</span>
            </Card.Header>
            <Card.Body><p>{question.text}</p></Card.Body>
          </Card>
        ))}
      </div>
    </Container>
  </section>
);

const Why = () => (
  <section className="fp-band fp-band-sunken">
    <Container className="fp-why">
      <div className="fp-sprawl">
        Agents arrive in a company one team at a time. Each team picks a framework, wires up its own model keys, and
        invents its own way to run, approve and log. The sum is a company that cannot say how many agents it runs, what
        they are allowed to do, or what one of them did last Tuesday.
        <span className="fp-eyebrow">Agent sprawl</span>
      </div>
      <div className="fp-principle">
        <span className="fp-eyebrow">Why BlobHub</span>
        <h2>BlobHub is to agents what the cloud was to servers: a new set of primitives to build against.</h2>
        <p>
          That surface makes agents, copilots and the apps that use them easy to design and build, and it is why most
          of what BlobHub promises is a property of the platform rather than a feature added to it.
        </p>
        <div className="fp-pills">{PRIMITIVES.map((primitive) => <Pill key={primitive}>{primitive}</Pill>)}</div>
        <div className="fp-grid fp-g2">
          <Card>
            <Card.Header>Almost everywhere else</Card.Header>
            <Card.Body>
              <p>
                The system that runs agents and the system that remembers what they did are separate products, joined
                by instrumentation.
              </p>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>BlobHub</Card.Header>
            <Card.Body>
              <p>
                Agents are made of the platform's primitives, so they live and work inside it. The record is written by
                the work itself.
              </p>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  </section>
);

const Pillars = () => (
  <>
    <Pillar
      kind="build" eyebrow="Before it runs" title="Build"
      lede="Workflows and workers, interoperable by construction."
      text={"Workflows are drawn in the editor or written as ordinary Python, versioned and deployed like code. "
        + "Workers run the coding agents your teams already use, Claude Code, Codex and Antigravity, on your own "
        + "machines, against the same sessions and threads. Everything made of the same primitives talks to "
        + "everything else without integration work."}
      rows={[
        ["A broad library of workflow building blocks: models, code, data, web, text and vector stores."],
        ["Author a workflow in the editor, or compile a Python package into one and deploy it from CI."],
        ["A worker on your own machine picks up agent work from a session's threads and reports back into them."],
        ["Sessions, threads, schedules and credentials are there on day one. You write the agent."],
      ]}
      caption={"The workflow behind Missions, assembled from the editor's library of building blocks: it locks, "
        + "loops a heartbeat through a sleep until its budget runs out and then unlocks. Beside it, a worker running "
        + "Claude Code, Codex and Antigravity threads on its own machine."}>
      <Shot pin={<Pill className="fp-pin">Workflow</Pill>}>
        <FrontShot name="build_node"
          alt={"The Missions orchestrator in the workflow editor, with the library of building blocks open beside it "
            + "(flow, data and logic steps): a lock step that goes to a retry step, a heartbeat code step that loops "
            + "through a sleep step, an unlock step, and data for the run's parameters, start time, budget and sleep "
            + "interval. The heartbeat step is selected, its data and flow inputs listed beside the graph."} />
      </Shot>
      <Shot float="worker" pin={<Pill className="fp-pin">Worker</Pill>}>
        <FrontShot name="worker_tui" alt={"The blobhub-worker dashboard holding Claude Code, Codex "
          + "and Antigravity threads in one session, with two of its four agent slots in use."} />
      </Shot>
    </Pillar>
    <Pillar
      kind="govern" flip eyebrow="While it runs" title="Govern"
      lede="Guardrails built in, not bolted on."
      text={"Coding agents run on your own machines through the BlobHub worker. In a guarded run the worker checks "
        + "each tool call the agent's harness escalates against a policy, and answers allow, deny or ask before the "
        + "call goes through. Permissions and credentials sit in the same system the agents act in."}
      rows={[
        ["Every run has a permission mode: approval, guarded or autonomous."],
        ["Policy rules over write, exec, read and network answer allow, deny or ask.",
          "A guardrail against mistakes, not a sandbox, enforced by the worker on the coding agents it runs."],
        ["An ask stops the run until a person answers, and the answer goes back into the run."],
        ["Roles decide who and what may read, write or administer; a machine key can never widen its own access."],
        ["Credentials resolve by alias at run time and are never readable back."],
      ]}
      caption={"A planning step tried to write; the worker's read_only policy stopped it. The rules come from the "
        + "policies Missions ships."}>
      <Shot pin={<Pill className="fp-pin">Worker thread</Pill>}>
        <FrontShot name="govern_policy" alt={"A worker transcript: a planning step tries to write a file, the policy "
          + "rule no_writes denies it, and the agent gets the denial back."} />
      </Shot>
      <div className="fp-comp-float fp-f-policy">
        <Pill className="fp-pin">policies.yaml</Pill>
        <CodeCard source={POLICY} />
      </div>
    </Pillar>
    <Pillar
      kind="audit" eyebrow="After it ran" title="Audit"
      lede="The record writes itself."
      text={"After the fact, whoever is accountable can reconstruct what an agent did, in what order and under whose "
        + "authority, without anyone having had to set anything up in advance."}
      rows={[
        ["Every run, every message and every approval is recorded because it happened here."],
        ["Every step entered and exited, with its route and its duration."],
        ["Every worker turn keeps its transcript: tool calls and results, policy decisions, tokens and time."],
        ["Debugging starts from the record, and so does anything you build on top of it."],
      ]}
      caption="One execution's events, and a worker's own record of the turn that built a feature.">
      <Shot pin={<Pill className="fp-pin">Debugger</Pill>}>
        <FrontShot name="audit_debugger" alt={"The debugger for one ticket's triage run: its overview, the workflow it "
          + "ran, and its events from the flow's start through the Route step that held the refund, with that hold "
          + "decision opened in the event details."} />
      </Shot>
      <Shot float="thread" pin={<Pill className="fp-pin">Worker thread</Pill>}>
        <FrontShot name="audit_thread" alt={"A worker's transcript of one turn: the agent's summary of what it built, "
          + "the files it published, and the turn's tokens and time."} />
      </Shot>
    </Pillar>
  </>
);

const Apps = () => (
  <section className="fp-band fp-band-sunken">
    <Container>
      <BandHead
        eyebrow="Apps" title="Apps built from the same primitives" to="/apps" more="All apps"
        text={"An app is built on BlobHub from its building blocks and deployed as content, with no platform code of "
          + "its own. Each one is governed and audited the same way."} />
      <div className="fp-grid fp-g3">
        <AppCard app={APPS.missions} />
        <AppCard app={APPS.daytrader} />
        <ComposeCard />
      </div>
    </Container>
  </section>
);

const Gallery = () => (
  <section className="fp-band">
    <Container>
      <BandHead
        eyebrow="Gallery" title="Start from a workflow that already runs" to="/gallery" more="Browse the gallery"
        text="Every entry is a public workflow you can open, fork into your own organization and run." />
      <div className="fp-grid fp-g4">
        {GALLERY.slice(0, 4).map((item) => <GalleryCard key={item.key} item={item} />)}
      </div>
    </Container>
  </section>
);

const Surfaces = () => (
  <section className="fp-band fp-band-sunken">
    <Container>
      <BandHead
        eyebrow="Surfaces" title="Five ways in, one API"
        text="Every action in the web app is a call to the same API, so nothing is reachable only through a screen." />
      <div className="fp-grid fp-g5">
        {SURFACES.map((surface) => (
          <Card key={surface.key} className="fp-surface">
            <Card.Header>{surface.title}</Card.Header>
            <Card.Body>
              <code className="fp-card-code">{surface.code}</code>
              <p className="fp-card-text"><SurfaceText surface={surface} /></p>
            </Card.Body>
          </Card>
        ))}
      </div>
      <div className="fp-code-pair">
        <CodeCard title="Terminal" file="blobhub" source={TERMINAL} />
        <CodeCard title="Python" file="run_session.py" source={PYTHON} />
      </div>
    </Container>
  </section>
);

const Labs = () => (
  <section className="fp-band">
    <Container>
      <BandHead
        eyebrow="Labs" title="One core, any kind of data" to="/labs" more="Open Labs"
        text={"Under every workflow sits the same core: typed blobs with revisions. It scales to other data "
          + "primitives, and some already run on it: ONNX models, knowledge graphs and NLP datasets, live in Labs."} />
    </Container>
  </section>
);

const Closing = ({ authenticated }) => (
  <Stage kind="closing" id="start" tabIndex={-1} labelledBy="start-title">
    <Container className="fp-closing-grid">
      <div>
        <h2 id="start-title">Put your first agent on BlobHub this afternoon.</h2>
        <p>
          An organization, a workflow and a session are three calls. The worker is one more, and your coding agents
          are in.
        </p>
      </div>
      <div className="hero-cta">
        <PrimaryCta authenticated={authenticated} />
        <External href={DOCS_URL} className="btn btn-light">Read the docs</External>
      </div>
    </Container>
  </Stage>
);

export default class Page extends Component {
  componentDidMount() {
    Title.setRoot();
  }

  scrollToStart = () => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const start = document.getElementById("start");
    if (start) {
      start.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      start.focus({ preventScroll: true });
    }
  };

  render() {
    const { app } = this.props;
    return (
      <div className="fp-page">
        <Hero onStart={this.scrollToStart} />
        <Position />
        <Why />
        <Pillars />
        <Apps />
        <Gallery />
        <Surfaces />
        <Labs />
        <Closing authenticated={Boolean(app && app.authenticated)} />
      </div>
    );
  }
}
